import { DateRange, Matcher, PropsBase, PropsRange, rangeContainsModifiers } from '@daypicker/react'
import { useState } from 'react'

import { add, day, diff, isAfter, isBefore, isBetween, isSame } from '../../../../../utils/extendedDayjs'

type FullDateRange = {
    from: Date
    to: Date
}

export const getWeekRange = (date: Date, weekdaysOnly?: boolean): FullDateRange => {
    const from = day(date).startOf('isoWeek')

    return {
        from: from.toDate(),
        to: add(from, weekdaysOnly ? 4 : 6, 'day').toDate(),
    }
}

export const getWeekSelectionRange = (selected: PropsRange['selected'], weekdaysOnly?: boolean): DateRange | undefined => {
    if (!selected?.from) return undefined

    return {
        from: getWeekRange(selected.from, weekdaysOnly).from,
        to: getWeekRange(selected.to ?? selected.from, weekdaysOnly).to,
    }
}

const getRangeFromDates = (from: Date, to: Date): FullDateRange => (isBefore(from, to) ? { from, to } : { from: to, to: from })

const hasDisabledDatesInRange = (disabled: PropsRange['disabled'], range: DateRange): boolean => {
    if (!disabled || !range.from || !range.to) return false
    return rangeContainsModifiers({ from: range.from, to: range.to }, disabled as Matcher | Matcher[])
}

const isFullSelectedRange = (selected: PropsRange['selected']): selected is FullDateRange => Boolean(selected?.from && selected.to)

const isSingleSelectedWeek = (selected: PropsRange['selected']): selected is FullDateRange =>
    isFullSelectedRange(selected) && isSame(selected.from, selected.to, 'isoWeek')

const getSelectedRange = (
    mode: PropsBase['mode'],
    selected: PropsRange['selected'],
    disabled: PropsRange['disabled'],
    date: Date,
    weekdaysOnly?: boolean
): DateRange | undefined => {
    const weekRange = getWeekRange(date, weekdaysOnly)

    if (isSingleSelectedWeek(selected) && isSame(weekRange.from, selected.from, 'isoWeek')) return undefined

    if (mode !== 'range' || !isFullSelectedRange(selected)) return weekRange

    const rangeToClickedWeek = getRangeFromDates(selected.from, weekRange.to)
    if (hasDisabledDatesInRange(disabled, rangeToClickedWeek)) return weekRange

    if (isSame(weekRange.from, selected.to, 'isoWeek')) return getWeekRange(selected.to, weekdaysOnly)
    if (isSame(weekRange.from, selected.from, 'isoWeek')) return getWeekRange(selected.from, weekdaysOnly)

    if (isBetween(weekRange.from, selected.from, selected.to, 'day', '[]')) {
        const fromDistance = Math.abs(diff(weekRange.from, selected.from, 'days'))
        const toDistance = Math.abs(diff(weekRange.to, selected.to, 'days'))

        return fromDistance < toDistance ? { from: weekRange.from, to: selected.to } : { from: selected.from, to: weekRange.to }
    }

    return {
        from: isBefore(weekRange.from, selected.from) ? weekRange.from : selected.from,
        to: isAfter(weekRange.to, selected.to) ? weekRange.to : selected.to,
    }
}

const getHoverRange = (
    mode: PropsBase['mode'],
    selected: PropsRange['selected'],
    disabled: PropsRange['disabled'],
    date: Date,
    weekdaysOnly?: boolean
): DateRange | undefined => {
    const weekRange = getWeekRange(date, weekdaysOnly)

    if (mode !== 'range' || !isFullSelectedRange(selected)) return weekRange
    if (isBetween(weekRange.from, selected.from, selected.to, 'day', '[]')) return undefined
    if (hasDisabledDatesInRange(disabled, getRangeFromDates(selected.from, weekRange.to))) return weekRange

    if (isBefore(weekRange.from, selected.from)) {
        return { from: weekRange.from, to: selected.from }
    }

    if (isAfter(weekRange.to, selected.to)) {
        return { from: selected.to, to: weekRange.to }
    }

    return undefined
}

export const useWeekSelectionHelpers = (
    mode: PropsBase['mode'],
    selected: PropsRange['selected'],
    onSelect: (range: DateRange | undefined) => void,
    disabled: PropsRange['disabled'],
    weekdaysOnly?: boolean
) => {
    const [hoverRange, setHoverRange] = useState<DateRange | undefined>(undefined)

    function handleSelect(date: Date | undefined) {
        setHoverRange(undefined)
        if (!date) return
        onSelect(getSelectedRange(mode, selected, disabled, date, weekdaysOnly))
    }

    function handleOnDayMouseEnter(date: Date | undefined) {
        if (!date) return
        setHoverRange(getHoverRange(mode, selected, disabled, date, weekdaysOnly))
    }

    function clearHoverRange() {
        setHoverRange(undefined)
    }

    return { clearHoverRange, handleOnDayMouseEnter, handleSelect, hoverRange }
}
